// src/pages/ReportsPage.jsx
// Analytics & Reports — collection trends, risk distribution, at-risk breakdown, ERV pipeline
import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  AreaChart, Area,
  BarChart, Bar,
  PieChart, Pie, Cell, // eslint-disable-line no-unused-vars
  XAxis, YAxis, Tooltip,
  ResponsiveContainer, CartesianGrid,
} from 'recharts';
import { useProperty } from '../hooks/useProperty';
import { useTenants } from '../hooks/useTenants';
import { formatCurrency } from '../utils/formatters';
import { exportAllOutreach } from '../utils/exportUtils';
import { calculatePortfolioERV, flattenOutreachLogs } from '../utils/ervEngine';
import { buildSentimentMap } from '../utils/sentimentAnalysis';

// ─── Brand colors ────────────────────────────────────────────────────────────
const GREEN   = '#0F6E56';
const AMBER   = '#EF9F27';
const RED     = '#DC2626'; // eslint-disable-line no-unused-vars
const GRAY    = '#9CA3AF';

// Risk-tier palette for charts
const TIER_COLORS = {
  high:    RED,
  medium:  AMBER,
  low:     GRAY,
  minimal: GREEN,
};

// ─── Helper: build last-N-months labels ───────────────────────────────────────
function buildMonthLabels(n = 12) {
  const months = [];
  const now = new Date();
  for (let i = n - 1; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    months.push({
      key: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`,
      label: d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' }),
    });
  }
  return months;
}

// ─── Helper: collection trend data ───────────────────────────────────────────
// For each of the last 12 months, sum payments made in that month across all
// tenants and compare to total rent roll as "expected".
function buildCollectionTrend(tenants, months) {
  const totalExpected = tenants.reduce((s, t) => s + (t.monthlyRent || 0), 0);
  if (totalExpected === 0) return months.map(m => ({ ...m, rate: 0, collected: 0 }));

  return months.map(m => {
    let collected = 0;
    tenants.forEach(tenant => {
      (tenant.paymentHistory || []).forEach(p => {
        if (!p.date || !p.amount) return;
        const pKey = p.date.slice(0, 7); // "YYYY-MM"
        if (pKey === m.key) collected += p.amount;
      });
    });
    const rate = Math.min(Math.round((collected / totalExpected) * 100), 100);
    return { ...m, rate, collected };
  });
}

// ─── Helper: days-late histogram buckets ─────────────────────────────────────
function buildDaysLateBuckets(tenants) {
  const buckets = [
    { label: 'On Time', count: 0, color: GREEN },
    { label: '1–7 days', count: 0, color: '#16A34A' },
    { label: '8–14 days', count: 0, color: AMBER },
    { label: '15–30 days', count: 0, color: '#F97316' },
    { label: '30+ days', count: 0, color: RED },
  ];
  tenants.forEach(t => {
    const d = t.currentDaysLate || 0;
    if (d === 0)       buckets[0].count++;
    else if (d <= 7)   buckets[1].count++;
    else if (d <= 14)  buckets[2].count++;
    else if (d <= 30)  buckets[3].count++;
    else               buckets[4].count++;
  });
  return buckets;
}

// ─── Reusable KPI card ───────────────────────────────────────────────────────
function KpiCard({ label, value, sub, accent = false }) {
  return (
    <div className={`rounded-2xl border p-5 ${accent ? 'bg-tenafi-green border-tenafi-green' : 'bg-white border-gray-200'}`}>
      <p className={`text-xs font-medium uppercase tracking-wider ${accent ? 'text-white/70' : 'text-gray-500'}`}>{label}</p>
      <p className={`text-2xl font-bold mt-1 ${accent ? 'text-white' : 'text-gray-900'}`}>{value}</p>
      {sub && <p className={`text-xs mt-1 ${accent ? 'text-white/60' : 'text-gray-400'}`}>{sub}</p>}
    </div>
  );
}

// ─── Custom Tooltip for AreaChart ─────────────────────────────────────────────
function TrendTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-lg px-4 py-3 text-sm">
      <p className="font-semibold text-gray-700 mb-1">{label}</p>
      <p className="text-tenafi-green font-bold">{payload[0]?.value}% collected</p>
      <p className="text-gray-400">{formatCurrency(payload[1]?.value || 0)} received</p>
    </div>
  );
}

// ─── Custom Tooltip for BarChart ─────────────────────────────────────────────
function DaysLateTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-lg px-4 py-3 text-sm">
      <p className="font-semibold text-gray-700">{label}</p>
      <p className="text-gray-600">{payload[0]?.value} tenants</p>
    </div>
  );
}


// ─── Main component ───────────────────────────────────────────────────────────
export default function ReportsPage() {
  const { properties, selectedProperty, setSelectedProperty } = useProperty();
  const { tenants, loading } = useTenants(selectedProperty?.id);

  const months = useMemo(() => buildMonthLabels(12), []);

  // ─── Helper: build recovery funnel from outreach + payment data ──────────────
  function buildRecoveryFunnel(tenants, months) {
    const now = new Date();
    const thisMonthKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    // Tenants with a balance (delinquent)
    const delinquent = tenants.filter(t => (t.currentBalance || 0) > 0);

    // Of those: received any outreach (non-payment channel)
    const contacted = delinquent.filter(t =>
      (t.outreachLog || []).some(e => e.channel !== 'payment' && e.channel !== 'payment_plan')
    );

    // Of those: tenant sent a response
    const responded = contacted.filter(t =>
      (t.outreachLog || []).some(e => e.responseText)
    );

    // Payments received this month across ALL tenants
    let dollarsRecoveredThisMonth = 0;
    let paymentsThisMonth = 0;
    tenants.forEach(t => {
      (t.outreachLog || []).forEach(e => {
        if (e.recoveryEvent && e.date?.slice(0, 7) === thisMonthKey) {
          dollarsRecoveredThisMonth += e.amount || 0;
          paymentsThisMonth++;
        }
      });
      // Also count from paymentHistory
      (t.paymentHistory || []).forEach(p => {
        if (p.date?.slice(0, 7) === thisMonthKey && p.recordedAt) {
          // Only count payments recorded this month (not historical import data)
        }
      });
    });

    // Tenants with balance fully cleared (balance was > 0 and now == 0)
    const cleared = tenants.filter(t =>
      (t.currentBalance || 0) === 0 &&
      (t.outreachLog || []).some(e => e.recoveryEvent)
    );

    // Recovery rate via outreach = payments recorded / delinquent tenants
    const conversionRate = contacted.length > 0
      ? Math.round((cleared.length / contacted.length) * 100)
      : 0;

    // 12-month recovery trend: dollars recovered per month
    const recoveryTrend = months.map(m => {
      let recovered = 0;
      tenants.forEach(t => {
        (t.outreachLog || []).forEach(e => {
          if (e.recoveryEvent && e.date?.slice(0, 7) === m.key) {
            recovered += e.amount || 0;
          }
        });
      });
      return { ...m, recovered };
    });

    return {
      delinquent: delinquent.length,
      contacted: contacted.length,
      responded: responded.length,
      cleared: cleared.length,
      dollarsRecoveredThisMonth,
      paymentsThisMonth,
      conversionRate,
      recoveryTrend,
    };
  }

  // --- Derived analytics (memoized) ---
  const analytics = useMemo(() => {
    if (!tenants.length) return null;

    const totalRent     = tenants.reduce((s, t) => s + (t.monthlyRent || 0), 0);
    const totalBalance  = tenants.reduce((s, t) => s + (t.currentBalance || 0), 0);
    const collected     = Math.max(totalRent - totalBalance, 0);
    const collectionRate = totalRent > 0 ? Math.round((collected / totalRent) * 100) : 100;
    const avgRisk       = Math.round(tenants.reduce((s, t) => s + (t.risk?.score || 0), 0) / tenants.length);

    // Tier counts for donut
    const tierCounts = ['high', 'medium', 'low', 'minimal'].map(tier => ({
      name: tier.charAt(0).toUpperCase() + tier.slice(1),
      value: tenants.filter(t => t.risk?.tier === tier).length,
      color: TIER_COLORS[tier],
    }));

    // At-risk dollars by tier (for bar chart)
    const tierBalances = ['high', 'medium', 'low', 'minimal'].map(tier => ({
      tier: tier.charAt(0).toUpperCase() + tier.slice(1),
      balance: tenants.filter(t => t.risk?.tier === tier).reduce((s, t) => s + (t.currentBalance || 0), 0),
      color: TIER_COLORS[tier],
    })).filter(t => t.balance > 0);

    // Monthly collection trend
    const trend = buildCollectionTrend(tenants, months);

    // Days-late distribution
    const daysLateBuckets = buildDaysLateBuckets(tenants);

    // Top 8 at-risk tenants
    const topAtRisk = [...tenants]
      .filter(t => t.risk?.tier === 'high' || t.risk?.tier === 'medium')
      .sort((a, b) => (b.risk?.score || 0) - (a.risk?.score || 0))
      .slice(0, 8);

    // ERV pipeline summary
    const outreachLogs = flattenOutreachLogs(tenants);
    const tenantContext = new Map(tenants.map(t => [t.id, { daysLate: t.currentDaysLate || 0, riskScore: t.risk?.score || 0 }]));
    const sentimentMap = buildSentimentMap(outreachLogs, tenantContext);
    const pipeline = calculatePortfolioERV(tenants, outreachLogs, sentimentMap);

    // Recovery funnel
    const funnel = buildRecoveryFunnel(tenants, months);

    return { totalRent, totalBalance, collected, collectionRate, avgRisk, tierCounts, tierBalances, trend, daysLateBuckets, topAtRisk, pipeline, funnel };
  }, [tenants, months]);

  // ── Loading state ──
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-tenafi-green border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // ── No property / no tenants ──
  if (!selectedProperty || !tenants.length) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <div className="text-5xl mb-4">📊</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">No data yet</h2>
          <p className="text-gray-500 mb-6">
            {!selectedProperty
              ? 'Select a property from the dashboard, then come back here to see analytics.'
              : 'Upload your rent roll or seed demo data to see collection analytics.'}
          </p>
          <Link to="/" className="px-6 py-3 bg-tenafi-green text-white rounded-xl font-semibold hover:bg-tenafi-green-dark transition-colors">
            ← Back to Dashboard
          </Link>
        </div>
      </div>
    );
  }

  const { totalRent, totalBalance, collected, collectionRate, avgRisk, tierCounts, tierBalances, trend, daysLateBuckets, topAtRisk, pipeline, funnel } = analytics;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* ── Page header ── */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-8">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Collection Analytics</h1>
            <p className="text-sm text-gray-500 mt-0.5">{selectedProperty.name} · {tenants.length} tenants</p>
          </div>
          <button
            onClick={() => exportAllOutreach(tenants, selectedProperty?.name)}
            className="px-4 py-2.5 border border-gray-300 text-gray-600 rounded-xl text-sm font-medium hover:bg-gray-50 transition-colors flex items-center gap-2">
            ↓ Export Outreach CSV
          </button>
          {properties.length > 1 && (
            <select
              value={selectedProperty?.id || ''}
              onChange={e => setSelectedProperty(properties.find(p => p.id === e.target.value))}
              className="border border-gray-300 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-tenafi-green outline-none bg-white"
            >
              {properties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          )}
        </div>

        {/* ── KPI Row ── */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <KpiCard
            label="Collection Rate"
            value={collectionRate + '%'}
            sub={'This month · ' + (collectionRate >= 95 ? '✅ On target' : collectionRate >= 80 ? '⚠️ Watch closely' : '🚨 Below target')}
            accent
          />
          <KpiCard
            label="Total Rent Roll"
            value={formatCurrency(totalRent)}
            sub="Expected this month"
          />
          <KpiCard
            label="Collected"
            value={formatCurrency(collected)}
            sub={formatCurrency(totalBalance) + ' outstanding'}
          />
          <KpiCard
            label="Avg Risk Score"
            value={avgRisk}
            sub={avgRisk >= 60 ? '🚨 High portfolio risk' : avgRisk >= 35 ? '⚠️ Moderate risk' : '✅ Portfolio healthy'}
          />
        </div>

        {/* ── ERV Pipeline Summary ── */}
        {pipeline && (
          <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="font-semibold text-gray-900">Expected Recovery Value (ERV)</h2>
                <p className="text-xs text-gray-400 mt-0.5">AI-weighted probability of recovering outstanding balances · <Link to="/engine" className="text-tenafi-green hover:underline">Open Decision Engine →</Link></p>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-tenafi-green">{formatCurrency(pipeline.totalRecoverable)}</p>
                <p className="text-xs text-gray-400">{pipeline.portfolioRecoveryRate}% recovery rate</p>
              </div>
            </div>
            {/* Pipeline stage breakdown */}
            <div className="grid grid-cols-3 gap-3 mb-4">
              {[
                { label: '🔮 Predict', count: pipeline.predict.count, erv: pipeline.predict.totalErv, color: 'border-blue-200 bg-blue-50', text: 'text-blue-700' },
                { label: '📊 Prioritize', count: pipeline.prioritize.count, erv: pipeline.prioritize.totalErv, color: 'border-amber-200 bg-amber-50', text: 'text-amber-700' },
                { label: '⚡ Execute', count: pipeline.execute.count, erv: pipeline.execute.totalErv, color: 'border-tenafi-green/30 bg-tenafi-light', text: 'text-tenafi-green' },
              ].map(s => (
                <div key={s.label} className={`rounded-xl border p-3 ${s.color}`}>
                  <p className={`text-xs font-bold ${s.text}`}>{s.label}</p>
                  <p className={`text-xl font-bold mt-1 ${s.text}`}>{s.count}</p>
                  <p className="text-[10px] text-gray-500">{formatCurrency(s.erv)} ERV</p>
                </div>
              ))}
            </div>
            {/* Recovery bar */}
            {pipeline.totalOutstanding > 0 && (
              <div>
                <div className="h-2.5 rounded-full bg-gray-100 overflow-hidden flex">
                  <div className="h-full bg-tenafi-green" style={{ width: `${(pipeline.execute.totalErv / pipeline.totalOutstanding) * 100}%` }} />
                  <div className="h-full bg-tenafi-amber" style={{ width: `${(pipeline.prioritize.totalErv / pipeline.totalOutstanding) * 100}%` }} />
                  <div className="h-full bg-blue-400" style={{ width: `${(pipeline.predict.totalErv / pipeline.totalOutstanding) * 100}%` }} />
                </div>
                <p className="text-[10px] text-gray-400 mt-1">{formatCurrency(pipeline.totalOutstanding)} total outstanding · {formatCurrency(pipeline.totalRecoverable)} expected to be recovered</p>
              </div>
            )}
          </div>
        )}

        {/* ── Recovery Funnel ── */}
        {funnel && (
          <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-6">
            <div className="flex items-center justify-between mb-5">
              <div>
                <h2 className="font-semibold text-gray-900">Recovery Funnel</h2>
                <p className="text-xs text-gray-400 mt-0.5">How outreach converts delinquent tenants into payments</p>
              </div>
              <div className="text-right">
                <p className="text-xl font-bold text-tenafi-green">{formatCurrency(funnel.dollarsRecoveredThisMonth)}</p>
                <p className="text-xs text-gray-400">recovered this month</p>
              </div>
            </div>

            {/* Funnel steps */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
              {[
                { label: 'Delinquent',  value: funnel.delinquent,  icon: '🚨', color: 'bg-red-50 border-red-200 text-red-600',    desc: 'Balance > $0' },
                { label: 'Contacted',   value: funnel.contacted,   icon: '📨', color: 'bg-amber-50 border-amber-200 text-amber-700', desc: 'Outreach sent' },
                { label: 'Responded',   value: funnel.responded,   icon: '💬', color: 'bg-blue-50 border-blue-200 text-blue-700',   desc: 'Tenant replied' },
                { label: 'Cleared',     value: funnel.cleared,     icon: '✅', color: 'bg-green-50 border-green-200 text-green-700', desc: 'Balance paid off' },
              ].map((step, i) => (
                <div key={step.label} className={`rounded-xl border p-3 ${step.color}`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-semibold uppercase tracking-wide opacity-70">{step.label}</span>
                    <span>{step.icon}</span>
                  </div>
                  <p className="text-2xl font-bold">{step.value}</p>
                  <p className="text-[10px] opacity-60 mt-0.5">{step.desc}</p>
                  {/* Drop-off arrow between steps */}
                  {i < 3 && funnel.delinquent > 0 && (
                    <p className="text-[10px] opacity-50 mt-1">
                      {i === 0 ? Math.round((funnel.contacted / Math.max(funnel.delinquent, 1)) * 100)
                        : i === 1 ? Math.round((funnel.responded / Math.max(funnel.contacted, 1)) * 100)
                        : Math.round((funnel.cleared / Math.max(funnel.responded, 1)) * 100)}% advance
                    </p>
                  )}
                </div>
              ))}
            </div>

            {/* Funnel bar */}
            {funnel.delinquent > 0 && (
              <div className="space-y-2">
                {[
                  { label: 'Delinquent', pct: 100, color: 'bg-red-400' },
                  { label: 'Contacted',  pct: Math.round((funnel.contacted / funnel.delinquent) * 100), color: 'bg-amber-400' },
                  { label: 'Responded', pct: Math.round((funnel.responded / funnel.delinquent) * 100), color: 'bg-blue-400' },
                  { label: 'Cleared',   pct: Math.round((funnel.cleared   / funnel.delinquent) * 100), color: 'bg-tenafi-green' },
                ].map(row => (
                  <div key={row.label} className="flex items-center gap-3">
                    <span className="w-20 text-xs text-gray-500 text-right shrink-0">{row.label}</span>
                    <div className="flex-1 h-4 rounded-full bg-gray-100 overflow-hidden">
                      <div className={`h-full rounded-full transition-all duration-700 ${row.color}`}
                        style={{ width: `${row.pct}%` }} />
                    </div>
                    <span className="w-10 text-xs text-gray-500 shrink-0">{row.pct}%</span>
                  </div>
                ))}
                <p className="text-[10px] text-gray-400 mt-2 text-center">
                  Overall conversion: <strong>{funnel.conversionRate}%</strong> of contacted tenants fully paid off their balance
                </p>
              </div>
            )}

            {funnel.delinquent === 0 && (
              <div className="text-center py-4 text-gray-400">
                <p className="text-sm">🎉 No delinquent tenants — all balances are current!</p>
              </div>
            )}
          </div>
        )}

        {/* ── Collection Trend ── */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-6">
          <div className="flex items-center justify-between mb-5">
            <div>
              <h2 className="font-semibold text-gray-900">12-Month Collection Trend</h2>
              <p className="text-xs text-gray-400 mt-0.5">Monthly payment received vs. expected rent roll</p>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={240}>
            <AreaChart data={trend} margin={{ top: 4, right: 4, left: -16, bottom: 0 }}>
              <defs>
                <linearGradient id="greenGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={GREEN} stopOpacity={0.18} />
                  <stop offset="95%" stopColor={GREEN} stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#9CA3AF' }} tickLine={false} axisLine={false} />
              <YAxis
                domain={[0, 100]}
                tickFormatter={v => v + '%'}
                tick={{ fontSize: 11, fill: '#9CA3AF' }}
                tickLine={false}
                axisLine={false}
              />
              <Tooltip content={<TrendTooltip />} />
              {/* Hidden area for collected dollars — used only in tooltip */}
              <Area type="monotone" dataKey="collected" stroke="transparent" fill="transparent" />
              {/* Main rate area */}
              <Area
                type="monotone"
                dataKey="rate"
                stroke={GREEN}
                strokeWidth={2.5}
                fill="url(#greenGrad)"
                dot={{ r: 3, fill: GREEN, strokeWidth: 0 }}
                activeDot={{ r: 5, fill: GREEN }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* ── Two-column row: Donut + Bar ── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">

          {/* Risk Distribution Donut */}
          <div className="bg-white rounded-2xl border border-gray-200 p-6">
            <h2 className="font-semibold text-gray-900 mb-1">Risk Distribution</h2>
            <p className="text-xs text-gray-400 mb-4">Tenants by current risk tier</p>
            <div className="flex items-center gap-4">
              <div className="flex-shrink-0">
                <ResponsiveContainer width={180} height={180}>
                  <PieChart>
                    <Pie
                      data={tierCounts}
                      cx="50%"
                      cy="50%"
                      innerRadius={52}
                      outerRadius={80}
                      paddingAngle={3}
                      dataKey="value"
                      startAngle={90}
                      endAngle={-270}
                    >
                      {tierCounts.map((entry, i) => (
                        <Cell key={i} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(v, name) => [v + ' tenants', name]} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="flex-1 space-y-3">
                {tierCounts.map(t => (
                  <div key={t.name} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: t.color }} />
                      <span className="text-sm text-gray-600">{t.name}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-gray-900">{t.value}</span>
                      <span className="text-xs text-gray-400">
                        ({tenants.length > 0 ? Math.round((t.value / tenants.length) * 100) : 0}%)
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Days-Late Distribution */}
          <div className="bg-white rounded-2xl border border-gray-200 p-6">
            <h2 className="font-semibold text-gray-900 mb-1">Days-Late Distribution</h2>
            <p className="text-xs text-gray-400 mb-4">How many tenants are currently overdue and by how much</p>
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={daysLateBuckets} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#9CA3AF' }} tickLine={false} axisLine={false} />
                <YAxis tick={{ fontSize: 11, fill: '#9CA3AF' }} tickLine={false} axisLine={false} allowDecimals={false} />
                <Tooltip content={<DaysLateTooltip />} />
                <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                  {daysLateBuckets.map((entry, i) => (
                    <Cell key={i} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* ── At-Risk Balance by Tier ── */}
        {tierBalances.length > 0 && (
          <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-6">
            <h2 className="font-semibold text-gray-900 mb-1">Outstanding Balance by Risk Tier</h2>
            <p className="text-xs text-gray-400 mb-5">Total dollars at risk, grouped by tenant risk level</p>
            <ResponsiveContainer width="100%" height={160}>
              <BarChart data={tierBalances} layout="vertical" margin={{ top: 0, right: 20, left: 40, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" horizontal={false} />
                <XAxis type="number" tickFormatter={v => '$' + (v >= 1000 ? Math.round(v / 1000) + 'k' : v)} tick={{ fontSize: 11, fill: '#9CA3AF' }} tickLine={false} axisLine={false} />
                <YAxis type="category" dataKey="tier" tick={{ fontSize: 12, fill: '#6B7280' }} tickLine={false} axisLine={false} />
                <Tooltip formatter={v => [formatCurrency(v), 'Outstanding']} />
                <Bar dataKey="balance" radius={[0, 6, 6, 0]}>
                  {tierBalances.map((entry, i) => (
                    <Cell key={i} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* ── Top At-Risk Tenants Table ── */}
        {topAtRisk.length > 0 && (
          <div className="bg-white rounded-2xl border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-5">
              <div>
                <h2 className="font-semibold text-gray-900">Top At-Risk Tenants</h2>
                <p className="text-xs text-gray-400 mt-0.5">Highest-risk tenants requiring immediate attention</p>
              </div>
              <Link to="/" className="text-tenafi-green text-sm font-medium hover:underline">
                View Action Feed →
              </Link>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 text-left">
                    <th className="pb-3 pr-4 font-medium text-gray-500">Tenant</th>
                    <th className="pb-3 pr-4 font-medium text-gray-500">Unit</th>
                    <th className="pb-3 pr-4 font-medium text-gray-500 text-right">Balance</th>
                    <th className="pb-3 pr-4 font-medium text-gray-500 text-right">Days Late</th>
                    <th className="pb-3 pr-4 font-medium text-gray-500">Risk</th>
                    <th className="pb-3 font-medium text-gray-500">Trend</th>
                  </tr>
                </thead>
                <tbody>
                  {topAtRisk.map(t => {
                    const tierColor = {
                      high: 'text-red-600 bg-red-50',
                      medium: 'text-amber-700 bg-amber-50',
                    }[t.risk?.tier] || 'text-gray-600 bg-gray-50';
                    const driftIcon = {
                      worsening: '↗',
                      slightly_worsening: '↗',
                      improving: '↘',
                      stable: '→',
                    }[t.drift?.direction] || '—';
                    const driftColor = t.drift?.direction?.includes('worsening')
                      ? 'text-red-500' : t.drift?.direction?.includes('improving')
                      ? 'text-green-600' : 'text-gray-400';
                    return (
                      <tr key={t.id} className="border-b border-gray-50 hover:bg-gray-50">
                        <td className="py-3 pr-4">
                          <Link
                            to={`/tenant/${selectedProperty.id}/${t.id}`}
                            className="font-medium text-gray-900 hover:text-tenafi-green transition-colors"
                          >
                            {t.name}
                          </Link>
                        </td>
                        <td className="py-3 pr-4 text-gray-500">{t.unit}</td>
                        <td className="py-3 pr-4 text-right font-medium text-red-600">{formatCurrency(t.currentBalance)}</td>
                        <td className="py-3 pr-4 text-right text-gray-600">{t.currentDaysLate || 0}d</td>
                        <td className="py-3 pr-4">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${tierColor}`}>
                            {t.risk?.score} · {t.risk?.tier}
                          </span>
                        </td>
                        <td className={`py-3 font-medium ${driftColor}`}>{driftIcon}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
